import { StockStatusIcon } from '@app/_components/StockStatusIcon';
import { SelectItem } from '@app/_components/ui/Select/Select';
import Image from 'next/image';
import type { Dictionary } from 'types';
import type { VariantAttribute } from 'types/variant';

import type { MarketConfiguration } from '@/config/markets/const';
import { getGooglePriceForVariant } from '@/utils/googlePricing';
import { formatPrice } from '@/utils/priceUtils';
import {
    hasSoonBackInStockStatus,
    hasStockItemStatus,
    selectableStockStatuses,
    showStatus,
} from '@/utils/variant/variantUtils';

export default function ProductPageVariantSelectItem({
    value,
    variantAttributes,
    dictionary,
    marketConfig,
}: {
    value: string;
    variantAttributes: VariantAttribute[];
    dictionary: Dictionary;
    marketConfig: MarketConfiguration;
}) {
    const variantAttribute = variantAttributes[0];
    const variantIsNotAvailable =
        variantAttributes.some((attribute) =>
            selectableStockStatuses.includes(attribute.stockStatus),
        ) === false;

    return (
        <SelectItem
            key={value}
            value={value}
            disabled={variantIsNotAvailable}
            hideIndicator
            className="min-h-10 lg:min-h-11"
            selectItemChildren={
                <div className="flex items-center gap-3">
                    <ProductPageVariantStockStatus
                        variantAttributes={variantAttributes}
                        dictionary={dictionary}
                    />
                    <ProductPageVariantPrice
                        variantAttributes={variantAttributes}
                        dictionary={dictionary}
                        marketConfig={marketConfig}
                    />
                </div>
            }>
            <div className="flex items-center gap-3">
                <ProductPageVariantSwatch variantAttribute={variantAttribute} />
                <span>{variantAttribute.label}</span>
            </div>
        </SelectItem>
    );
}

function ProductPageVariantStockStatus({
    variantAttributes,
    dictionary,
}: {
    variantAttributes: VariantAttribute[];
    dictionary: Dictionary;
}) {
    const variantHasStockItemStatus = hasStockItemStatus(variantAttributes);
    const variantIsSoonBackInStock =
        hasSoonBackInStockStatus(variantAttributes);
    const variantStockStatusText = showStatus(variantAttributes, dictionary);

    if (!variantHasStockItemStatus && !variantIsSoonBackInStock) {
        return null;
    }

    return (
        <span className="flex items-center gap-1.5">
            <StockStatusIcon />
            <span className="text-xxs uppercase">{variantStockStatusText}</span>
        </span>
    );
}

function ProductPageVariantPrice({
    variantAttributes,
    dictionary,
    marketConfig,
}: {
    variantAttributes: VariantAttribute[];
    dictionary: Dictionary;
    marketConfig: MarketConfiguration;
}) {
    if (!variantAttributes?.length) {
        return '-';
    }

    const prices = variantAttributes.map((attribute) => {
        const googlePrice = getGooglePriceForVariant(attribute.id);
        return {
            price: googlePrice ?? attribute.price.price,
            originalPrice: attribute.price.originalPrice,
            priceType: attribute.price.priceType,
        };
    });

    const variantsByPrice = prices.sort((a, b) => a.price - b.price);
    const variantWithLowestPrice = variantsByPrice.at(0);
    const variantWithHigestPrice = variantsByPrice.at(-1);

    if (!variantWithLowestPrice || !variantWithHigestPrice) {
        return '-';
    }

    return (
        <div className="flex flex-col-reverse">
            <span>
                {variantWithLowestPrice.price < variantWithHigestPrice.price &&
                    dictionary?.fromShortened}{' '}
                <span className="font-medium">
                    {formatPrice(
                        variantWithLowestPrice.price,
                        marketConfig.locale,
                    )}
                </span>
            </span>
            {variantWithLowestPrice.priceType === 'campaign' && (
                <span className="ml-2 text-xs text-grey-300 line-through">
                    {formatPrice(
                        variantWithLowestPrice.originalPrice ?? 0,
                        marketConfig.locale,
                    )}
                </span>
            )}
        </div>
    );
}

function ProductPageVariantSwatch({
    variantAttribute,
}: {
    variantAttribute: VariantAttribute;
}) {
    if (!variantAttribute.swatch) {
        return null;
    }

    return (
        <Image
            className="aspect-square h-7 w-7 object-cover"
            src={variantAttribute.swatch}
            width={50}
            height={50}
            alt={variantAttribute.code}
            sizes="50px"
        />
    );
}
